import { LANE_RALLY_BONUS, LANE_RALLY_RULES as R, laneRallyScore, type LaneRallyBonusEvent } from '../../../../shared/laneRallyRules';
import type { FinishPlayAttemptInput, LaneRallyQuestionResult } from '../../../../shared/results';
import type { RallyConfig } from './rallyLevels';
import { initialLane, laneCenters, nearestLane } from './laneLayout';
import { createRoadField, crossRoadField, roadFieldRules, type RoadObject } from './roadField';

/** Sürüş fiziği. Mesafeler metre, hızlar m/sn, süreler ms. */
export const DRIVE = {
  /** Gaz pedalı basılıyken seyir hızı: 95 km/sa (kullanıcı kararı 2026-09-27). Tabela mesafesi = öğretmen süresi × bu hız. */
  cruiseSpeed: 95 / 3.6,
  accel: 18,
  /** Pedal bırakılınca yavaşlama; araç yaklaşık 1 sn içinde durur. */
  coastDecel: 28,
  turboFactor: 1.35, turboMs: 1500,
  skidFactor: 0.6, skidMs: 1100,
  /** Engel çarpması: hız anında yarıya iner, kısa süre tavan düşer. Puana etkisi yoktur. */
  obstacleSpeedFactor: 0.5, obstacleSlowFactor: 0.7, obstacleSlowMs: 700,
  /** Tabelaya bu mesafeden az kalınca rota kilitlenir (seyir hızında ≈ 0,4 sn; şerit geçişi 280 ms'de biter). */
  lockDistance: 11,
  laneChangeMs: 280,
  /**
   * Tabela düzeni (kullanıcı kararı 2026-09-27; öğretmen süre ayarı yok). Gaz sürekli basılıyken iki tabela arası 11 sn:
   * geri bildirim 2 sn → altınlı yol geri bildirimin ortasında başlar ve 5 sn sürer → yeni soru açılır, tabelaya 5 sn.
   * Hepsi mesafeye bağlıdır: gaz bırakılınca araç, altınlar ve tabela durur; öğrenci istediği kadar düşünür.
   */
  approachSeconds: 5, fieldLeadSeconds: 1,
  /** Son sorudan sonra otomatik sürüşle bitiş kapısına varış. */
  arrivalMs: 2200,
  /** Sabit simülasyon adımı: her kare hızında aynı olaylar. */
  stepMs: 5,
} as const;

export type RallyPhase = 'ready' | 'approach' | 'committed' | 'feedback' | 'stretch' | 'arrival' | 'result';

export type DriveState = {
  phase: RallyPhase; clock: number; distance: number; speed: number; pedal: boolean;
  questionIndex: number; laneCount: number; lane: number; x: number; laneFrom: number; laneStarted: number;
  /** Geçerli sorunun tabelasının mutlak konumu ve soru açılışındaki toplam yaklaşma mesafesi. */
  gateDistance: number; gateTotal: number;
  questionStarted: number; changedLane: boolean; deadline: number;
  /** Son tabelanın geçildiği mesafe; altınlı yol bundan `fieldLeadSeconds` sonra başlar. */
  contactDistance: number; fieldActive: boolean;
  stretchIndex: number; stretchStart: number; field: RoadObject[];
  score: number; results: LaneRallyQuestionResult[];
  /** Altın bonusu: bilgi puanından (`score`) ayrı tutulur. */
  bonusPoints: number; bonusEvents: LaneRallyBonusEvent[][];
  turboUntil: number; skidUntil: number; slowUntil: number;
  lastEvent: { kind: LaneRallyBonusEvent; at: number } | null;
  laneChanges: number; accumulator: number; revision: number;
};

export function createDrive(config: RallyConfig): DriveState {
  if (!config.questions.length) throw new Error('Soru bulunamadı.');
  config.questions.forEach((q) => laneCenters(q.choices.length));
  const laneCount = config.questions[0].choices.length;
  const lane = initialLane(laneCount);
  const x = laneCenters(laneCount)[lane];
  return { phase: 'ready', clock: 0, distance: 0, speed: 0, pedal: false,
    questionIndex: 0, laneCount, lane, x, laneFrom: x, laneStarted: 0,
    gateDistance: Infinity, gateTotal: 0, questionStarted: 0, changedLane: false, deadline: Infinity, contactDistance: 0, fieldActive: false,
    stretchIndex: 0, stretchStart: 0, field: [], score: 0, results: [], bonusPoints: 0, bonusEvents: [],
    turboUntil: 0, skidUntil: 0, slowUntil: 0, lastEvent: null, laneChanges: 0, accumulator: 0, revision: 0 };
}

function openQuestion(s: DriveState, config: RallyConfig) {
  s.phase = 'approach'; s.gateTotal = DRIVE.approachSeconds * DRIVE.cruiseSpeed; s.gateDistance = s.distance + s.gateTotal;
  s.questionStarted = s.clock; s.changedLane = false; s.deadline = Infinity; s.revision++;
}

/** Öğrenci "Yarışa başla" dediğinde ilk tabela açılır; araç gaz pedalına basılana kadar durur. */
export function startDrive(s: DriveState, config: RallyConfig) {
  if (s.phase === 'ready') openQuestion(s, config);
}

export function setPedal(s: DriveState, pressed: boolean) {
  if (s.pedal === pressed) return;
  s.pedal = pressed; s.revision++;
}

const STEERABLE: readonly RallyPhase[] = ['approach', 'feedback', 'stretch'];

/** Şerit değiştirir. Kilitten sonra, varışta ve sonuçta reddedilir. */
export function steerLane(s: DriveState, lane: number): boolean {
  if (!STEERABLE.includes(s.phase) || !Number.isInteger(lane) || lane < 0 || lane >= s.laneCount || lane === s.lane) return false;
  s.laneFrom = s.x; s.laneStarted = s.clock; s.lane = lane; s.laneChanges++;
  if (s.phase === 'approach') s.changedLane = true;
  s.revision++;
  return true;
}
export function steerBy(s: DriveState, direction: -1 | 1): boolean { return steerLane(s, s.lane + direction); }

function contact(s: DriveState, config: RallyConfig) {
  const q = config.questions[s.questionIndex];
  const correctChoiceIndex = q.choices.findIndex((c) => c.correct);
  const correct = s.lane === correctChoiceIndex;
  const scoreDelta = laneRallyScore(correct); s.score += scoreDelta;
  s.results.push({ level: s.questionIndex + 1, questionId: q.id, choiceCount: q.choices.length,
    selectedChoiceIndex: s.lane, correctChoiceIndex, correct, scoreDelta, scoreAfter: s.score,
    durationMs: Math.round(s.clock - s.questionStarted), decision: s.changedLane ? 'manual' : 'automatic',
    questionPrompt: q.prompt || 'Görsel soru', selectedAnswer: q.choices[s.lane].label || 'Görsel seçenek',
    correctAnswer: q.choices[correctChoiceIndex].label || 'Görsel seçenek',
    questionMediaUrl: q.mediaUrl ?? null, selectedMediaUrl: q.choices[s.lane].mediaUrl ?? null, correctMediaUrl: q.choices[correctChoiceIndex].mediaUrl ?? null });
  if (correct) s.turboUntil = s.clock + DRIVE.turboMs; else s.skidUntil = s.clock + DRIVE.skidMs;
  s.phase = 'feedback'; s.deadline = s.clock + R.feedbackMs; s.gateDistance = Infinity; s.contactDistance = s.distance; s.revision++;
}

/** Altınlı yol: geri bildirim sürerken başlar; sonraki sorunun şerit düzenini kullanır. */
function beginField(s: DriveState, config: RallyConfig) {
  const nextCount = config.questions[s.questionIndex + 1].choices.length;
  if (nextCount !== s.laneCount) {
    // Yol sonraki sorunun şerit sayısına göre yeniden çizilir; araç en yakın yeni şeride kayar.
    s.laneCount = nextCount; s.lane = nearestLane(s.x, nextCount); s.laneFrom = s.x; s.laneStarted = s.clock;
  }
  s.fieldActive = true; s.stretchStart = s.distance;
  s.field = createRoadField(s.stretchIndex, s.laneCount); s.bonusEvents.push([]); s.revision++;
}

function collect(s: DriveState, fromAt: number, toAt: number) {
  const events = crossRoadField(s.field, fromAt, toAt, s.x, s.laneCount, s.clock);
  if (!events.length) return;
  const stretch = s.bonusEvents[s.bonusEvents.length - 1];
  for (const event of events) {
    const counted = stretch.filter((e) => e === event).length;
    if (counted >= (event === 'coin' ? LANE_RALLY_BONUS.coinsPerStretch : LANE_RALLY_BONUS.obstaclesPerStretch)) continue;
    stretch.push(event); s.lastEvent = { kind: event, at: s.clock };
    if (event === 'coin') s.bonusPoints += LANE_RALLY_BONUS.coinScore;
    else { s.speed *= DRIVE.obstacleSpeedFactor; s.slowUntil = s.clock + DRIVE.obstacleSlowMs; }
  }
  s.revision++;
}

function maxSpeed(s: DriveState) {
  return DRIVE.cruiseSpeed
    * (s.clock < s.turboUntil ? DRIVE.turboFactor : 1)
    * (s.clock < s.skidUntil ? DRIVE.skidFactor : 1)
    * (s.clock < s.slowUntil ? DRIVE.obstacleSlowFactor : 1);
}

function step(s: DriveState, config: RallyConfig, dtMs: number) {
  const dt = dtMs / 1000;
  s.clock += dtMs;
  const autopilot = s.phase === 'arrival';
  const throttle = autopilot || (s.phase !== 'result' && s.pedal);
  const limit = autopilot ? DRIVE.cruiseSpeed : maxSpeed(s);
  if (throttle) s.speed = s.speed < limit ? Math.min(limit, s.speed + DRIVE.accel * dt) : Math.max(limit, s.speed - DRIVE.coastDecel * dt);
  else s.speed = Math.max(0, s.speed - DRIVE.coastDecel * dt);
  const previous = s.distance;
  s.distance += s.speed * dt;
  const t = Math.min(1, (s.clock - s.laneStarted) / DRIVE.laneChangeMs);
  const target = laneCenters(s.laneCount)[s.lane];
  s.x = s.laneFrom + (target - s.laneFrom) * t * t * (3 - 2 * t);

  if (s.phase === 'approach' && s.gateDistance - s.distance <= DRIVE.lockDistance) { s.phase = 'committed'; s.revision++; }
  if (s.phase === 'committed' && s.distance >= s.gateDistance) contact(s, config);
  else if (s.phase === 'feedback' || s.phase === 'stretch') {
    const last = s.questionIndex === config.questions.length - 1;
    if (!last && !s.fieldActive && s.distance - s.contactDistance >= DRIVE.fieldLeadSeconds * DRIVE.cruiseSpeed) beginField(s, config);
    if (s.fieldActive) collect(s, previous - s.stretchStart, s.distance - s.stretchStart);
    if (s.phase === 'feedback' && s.clock >= s.deadline) {
      if (last) { s.phase = 'arrival'; s.deadline = s.clock + DRIVE.arrivalMs; }
      else { s.phase = 'stretch'; s.deadline = Infinity; }
      s.revision++;
    }
    if (s.phase === 'stretch' && s.fieldActive && s.distance - s.stretchStart >= roadFieldRules.stretchDistance) {
      s.stretchIndex++; s.field = []; s.fieldActive = false; s.questionIndex++;
      openQuestion(s, config);
    }
  } else if (s.phase === 'arrival' && s.clock >= s.deadline) { s.phase = 'result'; s.deadline = Infinity; s.revision++; }
}

/** Sabit adımlı entegrasyon: tek büyük debug adımı ile küçük kareler aynı olayları üretir. */
export function advanceDrive(s: DriveState, config: RallyConfig, milliseconds: number) {
  if (!Number.isFinite(milliseconds) || milliseconds <= 0 || s.phase === 'ready') return;
  s.accumulator += milliseconds;
  while (s.accumulator >= DRIVE.stepMs - 1e-6) {
    s.accumulator = Math.max(0, s.accumulator - DRIVE.stepMs);
    step(s, config, DRIVE.stepMs);
    if (s.phase === 'result' && s.speed === 0) { s.accumulator = 0; break; }
  }
}

export function rallyResult(s: DriveState, config: RallyConfig): FinishPlayAttemptInput {
  if (s.phase !== 'result' || s.results.length !== config.questions.length) throw new Error('Yarış henüz tamamlanmadı.');
  const correct = s.results.filter((r) => r.correct).length;
  const stretches = s.bonusEvents.map((stretch) => [...stretch]);
  const all = stretches.flat();
  return { score: s.score + s.bonusPoints, lives: 0, correct, wrong: s.results.length - correct, attempts: s.results.length, completed: true,
    details: { templateKey: 'lane-rally', questions: s.results.map((r) => ({ ...r })),
      bonus: { points: s.bonusPoints, coins: all.filter((e) => e === 'coin').length, obstacleHits: all.filter((e) => e === 'obstacle').length, stretches } } };
}
